/* eslint-disable @typescript-eslint/no-explicit-any */
import { TargetRegular, TargetAddRegular } from '@fluentui/react-icons';
import { useAccess } from '../../../../auth/useAccess';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { Column, BrandCard } from '../../../common';
import { useToasts } from '../../../toasts/useToasts';
import { Case, CaseMilestone, CaseStatus } from '../../../../data/types';
import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { useCaseServicesContext } from '../Cases/CaseServicesContext';
import { AddEditMilestoneDialog } from '../../../dialogs/AddEditMilestoneDialog';
import { CaseMilestonesTable } from './CaseMilestonesTable';
import { appTokens } from '../../../common/Styles/appTokens';
import { MilestoneProgress } from './MilestoneProgress';

const useStyles = makeStyles({
    field: {
        maxWidth: '100%',
    },
    emptyPanel: {
        paddingBlock: '1rem',
    },
    milestoneHeader: {
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    completedText: {
        pointerEvents: 'none',
        userSelect: 'none',
    },
});

export const CaseMilestonesPage = () => {
    const { caseForm, milestoneService, taskService, eventService, noteService } = useCaseServicesContext()!;

    //Vars
    const caseObj = caseForm.values as Case;
    const milestones = milestoneService.milestones;

    //Hooks
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);
    const toasts = useToasts();

    //Access
    const canCreate = () => {
        return access.cases.canCreateMilestones(caseObj.caseId);
    };
    const canEdit = () => {
        return access.cases.canCreateMilestones(caseObj.caseId) || access.cases.canEditMilestones(caseObj.caseId);
    };
    const canDelete = () => {
        return access.cases.canDeleteMilestones(caseObj.caseId);
    };
    const canReorder = () => {
        return access.cases.canReorderMilestones(caseObj.caseId);
    };
    const canReopen = () => {
        return access.cases.canReopenMilestones(caseObj.caseId);
    };

    //Logic
    const sortMilestones = (a: CaseMilestone, b: CaseMilestone) => {
        return (a.sortKey || 0) - (b.sortKey || 0);
    };
    const submitCaseMilestone = async (caseMilestone: CaseMilestone, noToast: boolean = false) => {
        if (canEdit() && caseMilestone.name) {
            try {
                await milestoneService.upsertCaseMilestoneAsync(caseMilestone);

                !noToast && toasts.caseMilestones.notifyCaseMilestoneCreated(caseMilestone);
            } catch {
                !noToast && toasts.caseMilestones.notifyCaseMilestoneCreateError(caseMilestone);
            }
        }
    };
    const moveMilestoneByOffset = async (milestone: CaseMilestone, offset: -1 | 1) => {
        if (!milestone) return;
        if (!canReorder()) return;

        const orderedMilestones = [...milestones]
            .map((m, index) => ({
                milestone: m,
                sortKey: m.sortKey ?? index,
                stableIndex: index,
            }))
            .sort((a, b) => a.sortKey - b.sortKey || a.stableIndex - b.stableIndex);

        const currentIndex = orderedMilestones.findIndex(({ milestone: current }) => {
            if (milestone.milestoneId) {
                return current.milestoneId === milestone.milestoneId;
            }

            return current === milestone;
        });

        if (currentIndex < 0) return;

        const targetIndex = currentIndex + offset;
        if (targetIndex < 0 || targetIndex >= orderedMilestones.length) return;

        const currentItem = orderedMilestones[currentIndex];
        const targetItem = orderedMilestones[targetIndex];
        if (currentItem.sortKey === targetItem.sortKey) return;

        const updatedCurrent: CaseMilestone = { ...currentItem.milestone, sortKey: targetItem.sortKey };
        const updatedTarget: CaseMilestone = { ...targetItem.milestone, sortKey: currentItem.sortKey };

        try {
            await Promise.all([submitCaseMilestone(updatedCurrent, true), submitCaseMilestone(updatedTarget, true)]);
        } catch {
            //nom nom nom
        }
    };
    const moveMilestoneUp = async (milestone: CaseMilestone) => {
        await moveMilestoneByOffset(milestone, -1);
    };
    const moveMilestoneDown = async (milestone: CaseMilestone) => {
        await moveMilestoneByOffset(milestone, 1);
    };

    //UI Events
    const onReopenMilestone = async (ms: CaseMilestone) => {
        if (!canReopen()) return;

        const msToBeReopened = milestones.filter((x) => (x.sortKey || 0) >= (ms.sortKey || 0));

        await Promise.all(
            msToBeReopened.map((ms: CaseMilestone) => {
                if (!ms.completed) return Promise.resolve(); //skip milestones that are not completed
                const updatedMs = { ...ms };
                updatedMs.completed = false;
                updatedMs.completedBy = '';
                updatedMs.completedOn = undefined;

                toasts.notifyInfo('Milestone Reopened', `Milestone '${ms.name}' has been reopened.`);

                return submitCaseMilestone(updatedMs, true);
            }),
        );

        //milestoneService.refreshMilestones();
    };
    const changeCaseStatusFromMilestone = (status: CaseStatus) => {
        caseForm.setFieldValue('status', status);
        caseForm.submitForm();
    };

    const orderedMilestones = [...milestones].sort(sortMilestones);

    return (
        <Column flex='1' padding='px-2 py-1'>
            <BrandCard
                flex='1'
                title='Milestones'
                headerIcon={<TargetRegular />}
                action={
                    <>
                        {!!canCreate() && (
                            <Column padding='pr-3'>
                                <AddEditMilestoneDialog
                                    submitMilestone={(ms) => milestoneService.upsertCaseMilestoneAsync(ms, true)}
                                    onChangeStatus={changeCaseStatusFromMilestone}
                                >
                                    <Button
                                        icon={<TargetAddRegular />}
                                        appearance='secondary'
                                        //onClick={clickCreateMilestone}
                                        data-testid='case-milestones-add-milestone-button'
                                    >
                                        Add Milestone
                                    </Button>
                                </AddEditMilestoneDialog>
                            </Column>
                        )}
                    </>
                }
            >
                <Column flex='1' padding='pl-1 pr-3'>
                    <MilestoneProgress milestones={orderedMilestones} />
                    <CaseMilestonesTable 
                        bodyHeight={appTokens.pageHeight(orderedMilestones.length > 0 ? '25.5rem' : '17.5rem')}
                        milestones={orderedMilestones}
                        taskService={taskService}
                        eventService={eventService}
                        noteService={noteService}
                        onSubmitMilestone={(ms) => milestoneService.upsertCaseMilestoneAsync(ms, true)}
                        onDeleteMilestone={(ms) => milestoneService.deleteCaseMilestoneAsync(ms.milestoneId!, ms.caseId, true)}
                        changeCaseStatusFromMilestone={changeCaseStatusFromMilestone}
                        onReopen={onReopenMilestone}
                        onMoveUp={moveMilestoneUp}
                        onMoveDown={moveMilestoneDown}
                    />
                </Column>
            </BrandCard>
        </Column>
    );
};

// type MilestoneControlProps = {
//     milestone?: CaseMilestone;
//     milestoneCount?: number;
//     submitMilestone?: (milestone: CaseMilestone) => Promise<void>;
//     taskService: CaseTaskService;
//     eventService: CaseEventService;
//     onMoveUp?: (milestone: CaseMilestone) => Promise<void>;
//     onMoveDown?: (milestone: CaseMilestone) => Promise<void>;
//     onDeleteMilestone?: (milestone: CaseMilestone) => Promise<void>;
//     current?: boolean;
//     onReopen?: () => void;
//     onChangeStatus?: (status: CaseStatus) => void;
//     statuses?: CaseStatus[];
//     caseRetainedDate?: Date;
// };
// const MilestoneControl = (props: MilestoneControlProps) => {
//     const {
//         milestone,
//         milestoneCount,
//         submitMilestone,
//         taskService,
//         eventService,
//         onMoveUp,
//         onMoveDown,
//         onDeleteMilestone,
//         current,
//         onReopen,
//         statuses,
//         onChangeStatus,
//         caseRetainedDate,
//     } = props;

//     //Hooks
//     const { primaryAccount } = useAccountState();
//     const access = useAccess(primaryAccount?.user);
//     const initValues = useInitialValues();
//     const timeTrackingService = useTimeTrackingService();
//     const utils = useUtilities();
//     const userUtils = useUserUtilities();
//     const messageDisplay = useAppMessageDisplay();
//     const caseDataService = useCaseDataService();
//     const { validateCaseMilestone } = useValidation();

//     //State
//     const [closeTasksOnRemove, setCloseTasksOnRemove] = useState<boolean>(true);
//     const [deleteEventsOnRemove, setDeleteEventsOnRemove] = useState<boolean>(true);
//     const [reopenConfirmOpen, setReopenConfirmOpen] = useState<boolean>(false);
//     const [caseStatuses, setCaseStatuses] = useState<CaseStatus[]>([]);

//     //Styles
//     const commonStyles = useCommonStyles();
//     const styles = useStyles();
//     const fieldClasses = mergeClasses(styles.field, commonStyles.text.field);
//     const completedTextClasses = mergeClasses(styles.completedText, commonStyles.text.subtleText);

//     //Form
//     const hasRequiredFields = (msValues: CaseMilestone) => {
//         return !!msValues.milestoneId && !!msValues.name;
//     };
//     const milestoneForm = useFormik<CaseMilestone>({
//         initialValues: {
//             ...initValues.caseMilestone(),
//             ...milestone,
//             dueDate: utils.date.validDate(milestone?.dueDate),
//         },
//         onSubmit: async (values, { resetForm }) => {
//             const msValues = values as CaseMilestone;
//             if (!hasRequiredFields(msValues)) {
//                 messageDisplay.addMessage(getMissingFieldsMsg(msValues));
//             } else {
//                 try {
//                     !!submitMilestone && submitMilestone(msValues);
//                 } catch {
//                     //nom nom nom
//                 }
//             }
//             resetForm({ values });
//         },
//         validationSchema: validateCaseMilestone.schema,
//     });
//     const milestoneObj = milestoneForm.values;

//     //Messages
//     const getMissingFieldsMsg = (msValues: CaseMilestone): AppMessage => {
//         return {
//             id: 'missingFields',
//             intent: 'error',
//             title: 'Missing required fields:',
//             content: (
//                 <>
//                     {!msValues.name && (
//                         <Text>
//                             <Text italic>'Milestone Name'</Text> is a required field.{' '}
//                         </Text>
//                     )}
//                 </>
//             ),
//         };
//     };

//     //Access
//     const canEdit = () => {
//         return (
//             !!milestone &&
//             (access.cases.canCreateMilestones(milestoneObj.caseId) || access.cases.canEditMilestones(milestoneObj.caseId))
//         );
//     };
//     const canDelete = () => {
//         return access.cases.canDeleteMilestones(milestoneObj.caseId);
//     };
//     const canReorder = () => {
//         return access.cases.canReorderMilestones(milestoneObj.caseId);
//     };
//     const canAccessComplete = () => {
//         return access.cases.canCompleteMilestones(milestoneObj.caseId);
//     };
//     const canReopen = () => {
//         return milestoneObj.completed && access.cases.canReopenMilestones(milestoneObj.caseId);
//     };

//     //Logic
//     const filterTasksForMilestone = (task: Task) => {
//         return task.milestoneId == milestoneObj?.milestoneId;
//     };
//     const filterOpenTasks = (task: Task) => {
//         return task.status == TaskStatus.open;
//     };
//     const filterEventsForMilestone = (event: CaseEvent) => {
//         return event.milestoneId == milestoneObj?.milestoneId;
//     };
//     const readyToComplete = () => {
//         return (
//             current &&
//             !milestoneObj.completed &&
//             milestoneObj.deliverablesDelivered &&
//             taskService.tasks.filter(filterTasksForMilestone).filter(filterOpenTasks).length == 0 &&
//             eventService.currentEvents.filter(filterEventsForMilestone).length == 0
//         );
//     };
//     const canBeCompleted = () => {
//         if (primaryAccount?.account.settings?.cannotCompleteMilestoneBeforePrevious) {
//             return current && !milestoneObj.completed;
//         } else {
//             return true;
//         }
//     };
//     const isOverdue = () => {
//         const today = new Date();
//         return !!milestoneObj.dueDate && today > new Date(milestoneObj.dueDate) && !milestoneObj.completed;
//     };
//     const getCompleteBtnStyle = (): CSSProperties => {
//         if (milestoneObj.completed) {
//             return {
//                 backgroundColor: tokens.colorStatusSuccessBackground3,
//                 border: '1px solid white',
//             };
//         } else if (readyToComplete()) {
//             return { backgroundColor: 'lightgreen', border: '1px solid green' };
//         } else if (isOverdue()) {
//             return {
//                 backgroundColor: tokens.colorPaletteYellowBackground3,
//                 border: '1px solid #555',
//             };
//         } else {
//             return {};
//         }
//     };
//     const sortMilestoneTasks = (a: Task, b: Task) => {
//         // Primary sort: by status
//         const aStat = Object.values(TaskStatus).indexOf(a.status as TaskStatus);
//         const bStat = Object.values(TaskStatus).indexOf(b.status as TaskStatus);
//         const statusComparison = aStat - bStat;
//         if (statusComparison !== 0) {
//             return statusComparison;
//         }

//         // Secondary sort: by due date (if available)
//         if (a.lifecycle?.createdOn && b.lifecycle?.createdOn) {
//             return new Date(a.lifecycle.createdOn).getTime() - new Date(b.lifecycle.createdOn).getTime();
//         }

//         return 0;
//     };

//     //UI Events
//     const clickMoveUp = (e: React.MouseEvent) => {
//         e.stopPropagation();

//         if (!canReorder()) return;
//         !!onMoveUp && onMoveUp(milestone!);
//     };
//     const clickMoveDown = (e: React.MouseEvent) => {
//         e.stopPropagation();

//         if (!canReorder()) return;
//         !!onMoveDown && onMoveDown(milestone!);
//     };
//     const clickRemove = async (e?: React.MouseEvent) => {
//         e?.stopPropagation();

//         if (!canDelete()) return;

//         if (!!closeTasksOnRemove) {
//             //close the remaining open tasks for this milestone
//             const msTasks = taskService?.tasks?.filter(filterTasksForMilestone).filter(filterOpenTasks);

//             try {
//                 await Promise.all(
//                     msTasks.map((task: Task) => {
//                         const editedTask = { ...task, milestoneId: '' };
//                         return taskService.onClose(editedTask);
//                     }),
//                 );
//             } catch (error) {
//                 console.log(error);
//             }
//         }

//         if (!!deleteEventsOnRemove) {
//             //delete the events for this milestone
//             const msEvents = eventService.events.filter(filterEventsForMilestone);

//             try {
//                 await Promise.all(
//                     msEvents.map((event: CaseEvent) => {
//                         return eventService.deleteCaseEventAsync(event.eventId!, event.caseId);
//                     }),
//                 );
//             } catch (error) {
//                 console.log(error);
//             }
//         }

//         !!onDeleteMilestone && onDeleteMilestone(milestone!);
//     };
//     const onSelectDate = (date: Date | null | undefined) => {
//         milestoneForm.setFieldValue('dueDate', date || undefined);
//     };
//     const clickComplete = async (e?: React.MouseEvent) => {
//         e?.stopPropagation?.();

//         if (!canAccessComplete()) return;

//         milestoneForm.setFieldValue('completed', true);
//         milestoneForm.setFieldValue('completedBy', primaryAccount?.user?.username);
//         milestoneForm.setFieldValue('completedOn', new Date());

//         milestoneForm.setFieldValue('deliverablesDelivered', true);
//         milestoneForm.submitForm();

//         //complete remaining open tasks for this milestone
//         const msTasks = taskService?.tasks?.filter(filterTasksForMilestone).filter(filterOpenTasks);
//         try {
//             await Promise.all(
//                 msTasks.map((task: Task) => {
//                     return taskService.onComplete(task);
//                 }),
//             );
//         } catch {
//             //nom nom nom
//         }
//     };
//     const clickReopen = () => {
//         if (!canReopen()) return;

//         !!onReopen && onReopen();
//     };
//     const onUpdateMilestoneClick = (e: React.MouseEvent<HTMLButtonElement>) => {
//         e.stopPropagation();
//         milestoneForm.submitForm();
//     };
//     const onCloseTasksOnRemoveChange = (e: any, data: CheckboxOnChangeData) => {
//         e?.stopPropagation?.();
//         setCloseTasksOnRemove(!!data.checked);
//     };
//     const onDeleteEventsOnRemoveChange = (e: any, data: CheckboxOnChangeData) => {
//         e?.stopPropagation?.();
//         setDeleteEventsOnRemove(!!data.checked);
//     };
//     const onRemoveCheckboxClick = (e: React.MouseEvent<HTMLElement>) => {
//         e.stopPropagation();
//     };
//     const onDeliverablesDeliveredChange = (e: any, data: CheckboxOnChangeData) => {
//         e?.stopPropagation?.();
//         milestoneForm.setFieldValue('deliverablesDelivered', data.checked);
//     };
//     const onReopenConfirmOpen = (...args: any[]) => {
//         const maybeEvent = args[0];
//         const maybeOpen = typeof args[0] === 'boolean' ? args[0] : args?.[1]?.open;

//         maybeEvent?.stopPropagation?.();
//         if (typeof maybeOpen === 'boolean') {
//             setReopenConfirmOpen(maybeOpen);
//         }
//     };
//     const onOpenReopenConfirmClick = (e: any) => {
//         e?.stopPropagation?.();
//         setReopenConfirmOpen(true);
//     };
//     const onDeleteEvent = (ev: CaseEvent) => {
//         return eventService.deleteCaseEventAsync(ev.eventId!, milestoneObj?.caseId as string);
//     };
//     const completeAndChangeStatus = (stat: CaseStatus) => {
//         if (!canAccessComplete()) return;

//         clickComplete();
//         !!onChangeStatus && onChangeStatus(stat);
//     };

//     //Effects
//     useEffect(() => {
//         if (statuses?.length) {
//             setCaseStatuses(caseDataService.getTheseStatuses(statuses) || []);
//         } else {
//             setCaseStatuses(caseDataService.caseStatuses);
//         }
//     }, [statuses]);

//     useEffect(() => {
//         milestoneForm.resetForm({
//             values: {
//                 ...initValues.caseMilestone(),
//                 ...milestone,
//                 dueDate: utils.date.validDate(milestone?.dueDate),
//             },
//         });
//     }, [milestone?.milestoneId, milestone?.completed]);

//     return (
//         <AccordionItem className={commonStyles.utility.withHiddenChildren} value={milestoneObj.order}>
//             <AccordionHeader size='extra-large'>
//                 <Row className={styles.milestoneHeader} padding='pr-3'>
//                     <Column vAlign='middle'>
//                         <Row padding='py-1'>
//                             <Column width='auto'>
//                                 <Text size={500}>
//                                     #{(milestoneObj.order || 0) + 1} - {milestoneObj.name}
//                                 </Text>
//                             </Column>
//                             <Column>
//                                 <AppMessageDisplay
//                                     messages={messageDisplay.messages}
//                                     onDismiss={messageDisplay.removeMessage}
//                                 />
//                                 {/* <Text size={200}>{JSON.stringify(milestoneForm.errors)}</Text> */}
//                             </Column>
//                             <Column vAlign='middle' align='right'>
//                                 <Row gap='medium' width='auto'>
//                                     {milestoneObj?.completed &&
//                                         !!taskService?.tasks
//                                             ?.filter(filterTasksForMilestone)
//                                             .some((x) => x.status == TaskStatus.open) && (
//                                             <Badge appearance='filled' size='large' color='danger'>
//                                                 Open Tasks
//                                             </Badge>
//                                         )}
//                                     {!!milestoneObj.completed && (
//                                         <Badge appearance='tint' size='large' color='success'>
//                                             Complete
//                                         </Badge>
//                                     )}
//                                     {current && !milestoneObj.completed && (
//                                         <>
//                                             {isOverdue() ? (
//                                                 <Badge appearance='filled' size='large' color='danger'>
//                                                     In Progress - Overdue
//                                                 </Badge>
//                                             ) : (
//                                                 <Badge appearance='tint' size='large' color='brand'>
//                                                     In Progress
//                                                 </Badge>
//                                             )}
//                                         </>
//                                     )}
//                                 </Row>
//                             </Column>
//                         </Row>
//                     </Column>
//                     <Column align='right' width='auto' vAlign='middle'>
//                         {canEdit() && (
//                             <Button
//                                 appearance='primary'
//                                 size='small'
//                                 onClick={onUpdateMilestoneClick}
//                                 disabled={!milestoneForm.dirty || !milestoneForm.isValid}
//                                 data-testid='case-milestones-update-milestone-button'
//                             >
//                                 Update Milestone
//                             </Button>
//                         )}
//                     </Column>
//                     <Column width='auto' vAlign='middle'>
//                         <Row gap='small'>
//                             {canReorder() && (
//                                 <>
//                                     <Tooltip content='Move Milestone Up' relationship='label'>
//                                         <Button
//                                             className='hidden'
//                                             size='small'
//                                             icon={<ChevronDoubleUpRegular />}
//                                             onClick={clickMoveUp}
//                                             disabled={milestoneObj?.order == 0}
//                                             data-testid='case-milestones-move-up-button'
//                                         />
//                                     </Tooltip>
//                                     <Tooltip content='Move Milestone Down' relationship='label'>
//                                         <Button
//                                             className='hidden'
//                                             size='small'
//                                             icon={<ChevronDoubleDownRegular />}
//                                             onClick={clickMoveDown}
//                                             disabled={!!milestoneCount && milestoneObj?.order == milestoneCount - 1}
//                                             data-testid='case-milestones-move-down-button'
//                                         />
//                                     </Tooltip>
//                                 </>
//                             )}
//                             {canDelete() && (
//                                 <Column padding='pl-2'>
//                                     <Tooltip content='Remove Milestone' relationship='label'>
//                                         <ConfirmationDialog
//                                             title='Confirm Delete Milestone'
//                                             text={`Are you sure you want to delete the "${milestoneObj.name}" milestone?`}
//                                             onConfirm={clickRemove}
//                                             action={
//                                                 <Row>
//                                                     <Checkbox
//                                                         checked={closeTasksOnRemove}
//                                                         onChange={onCloseTasksOnRemoveChange}
//                                                         onClick={onRemoveCheckboxClick}
//                                                         label='Close open tasks?'
//                                                         data-testid='case-milestones-close-open-tasks-checkbox'
//                                                     />
//                                                     <Checkbox
//                                                         checked={deleteEventsOnRemove}
//                                                         onChange={onDeleteEventsOnRemoveChange}
//                                                         onClick={onRemoveCheckboxClick}
//                                                         label='Delete current events?'
//                                                         data-testid='case-milestones-delete-current-events-checkbox'
//                                                     />
//                                                 </Row>
//                                             }
//                                         >
//                                             <Button
//                                                 className='hidden'
//                                                 size='small'
//                                                 icon={<DismissFilled color='red' />}
//                                                 data-testid='case-milestones-remove-milestone-button'
//                                             />
//                                         </ConfirmationDialog>
//                                     </Tooltip>
//                                 </Column>
//                             )}
//                         </Row>
//                     </Column>
//                 </Row>
//             </AccordionHeader>
//             <AccordionPanel>
//                 <Column height='calc(100% - 10rem)' padding='pl-4 pb-3' gap='medium' scroll>
//                     <Row>
//                         <Column gap='medium' flex='2'>
//                             <Field
//                                 label='Milestone Name'
//                                 required={!!canEdit()}
//                                 className={fieldClasses}
//                                 validationMessage={milestoneForm.errors.name}
//                             >
//                                 <Input
//                                     name='name'
//                                     value={milestoneObj.name}
//                                     onChange={milestoneForm?.handleChange}
//                                     tabIndex={1}
//                                     placeholder='Enter the name of this milestone'
//                                     readOnly={!canEdit()}
//                                     appearance={!canEdit() ? 'underline' : undefined}
//                                     data-testid='case-milestones-name-input'
//                                 />
//                             </Field>
//                             <Field
//                                 label='Description'
//                                 className={fieldClasses}
//                                 validationMessage={milestoneForm.errors.description}
//                                 hint={`${milestoneObj.description?.length || 0} / ${validateCaseMilestone.description.maxLength}`}
//                             >
//                                 <Textarea
//                                     name='description'
//                                     value={milestoneObj.description}
//                                     onChange={milestoneForm?.handleChange}
//                                     tabIndex={2}
//                                     rows={3}
//                                     readOnly={!canEdit()}
//                                     placeholder={`Enter a description of this milestone`}
//                                     data-testid='case-milestones-description-textarea'
//                                     maxLength={validateCaseMilestone.description.maxLength}
//                                 />
//                             </Field>
//                         </Column>
//                         <Column flex='2'>
//                             <Field
//                                 className={fieldClasses}
//                                 label={
//                                     <Row>
//                                         <Column>
//                                             <InfoLabel
//                                                 label='Milestone Deliverables'
//                                                 info={
//                                                     <Text size={200}>
//                                                         Make a note of the deliverables expected by the end of this
//                                                         milestone.
//                                                     </Text>
//                                                 }
//                                             />
//                                         </Column>
//                                         <Column width='auto'>
//                                             <Checkbox
//                                                 label='Delivered?'
//                                                 checked={milestoneObj.deliverablesDelivered}
//                                                 onChange={onDeliverablesDeliveredChange}
//                                                 data-testid='case-milestones-delivered-checkbox'
//                                             />
//                                         </Column>
//                                     </Row>
//                                 }
//                                 hint={`${milestoneObj.deliverables?.length || 0} / ${validateCaseMilestone.deliverables.maxLength}`}
//                             >
//                                 <Textarea
//                                     name='deliverables'
//                                     value={milestoneObj.deliverables}
//                                     onChange={milestoneForm?.handleChange}
//                                     tabIndex={3}
//                                     rows={6}
//                                     readOnly={!canEdit()}
//                                     placeholder={`Enter a description of the deliverables of this milestone`}
//                                     data-testid='case-milestones-deliverables-textarea'
//                                     maxLength={validateCaseMilestone.deliverables.maxLength}
//                                 />
//                             </Field>
//                         </Column>
//                         <Column flex='1' gap='small' align='left'>
//                             <Field
//                                 className={fieldClasses}
//                                 label={
//                                     <InfoLabel
//                                         label='Due Date'
//                                         info={
//                                             <Text size={200}>
//                                                 Optionally set a due date for the Milestone to be completed.
//                                             </Text>
//                                         }
//                                     />
//                                 }
//                             >
//                                 <DatePicker
//                                     name='dueDate'
//                                     value={milestoneObj.dueDate}
//                                     onSelectDate={onSelectDate}
//                                     placeholder='Select a date...'
//                                     readOnly={!canEdit()}
//                                     minDate={caseRetainedDate ? new Date(caseRetainedDate) : undefined}
//                                     data-testid='case-milestones-due-date-picker'
//                                 />
//                             </Field>
//                         </Column>
//                         <Column flex='1' noGap align='right' padding='py-3'>
//                             <Column style={{ position: 'relative' }}>
//                                 <Menu positioning='below-end'>
//                                     <MenuTrigger disableButtonEnhancement>
//                                         {(triggerProps: MenuButtonProps) => (
//                                             <SplitButton
//                                                 menuButton={{
//                                                     ...triggerProps,
//                                                     appearance: milestoneObj.completed ? 'primary' : 'secondary',
//                                                     style: getCompleteBtnStyle(),
//                                                     disabled: (!canAccessComplete() || !canBeCompleted()) && !canReopen(),
//                                                 }}
//                                                 primaryActionButton={{
//                                                     onClick: clickComplete,
//                                                     style: getCompleteBtnStyle(),
//                                                     appearance: milestoneObj.completed ? 'primary' : 'secondary',
//                                                     icon: milestoneObj.completed ? (
//                                                         <CheckmarkFilled />
//                                                     ) : (
//                                                         <CheckboxUncheckedRegular />
//                                                     ),
//                                                     disabled: !canBeCompleted() || !canAccessComplete(),
//                                                 }}
//                                             >
//                                                 {milestoneObj.completed ? 'Completed' : 'Complete Milestone'}
//                                             </SplitButton>
//                                         )}
//                                     </MenuTrigger>

//                                     <MenuPopover>
//                                         <MenuList>
//                                             {!milestoneObj.completed ? (
//                                                 <>
//                                                     <MenuItem
//                                                         onClick={clickComplete}
//                                                         icon={<CheckmarkRegular />}
//                                                         disabled={!canAccessComplete()}
//                                                         data-testid='case-milestones-complete-menu-item'
//                                                     >
//                                                         Complete Milestone
//                                                     </MenuItem>

//                                                     {!!statuses?.length && !!onChangeStatus && (
//                                                         <Menu>
//                                                             <MenuTrigger disableButtonEnhancement>
//                                                                 <MenuItem
//                                                                     icon={<StatusRegular />}
//                                                                     disabled={!canAccessComplete()}
//                                                                     data-testid='case-milestones-complete-change-status-menu-item'
//                                                                 >
//                                                                     Complete + Change Status
//                                                                 </MenuItem>
//                                                             </MenuTrigger>
//                                                             <MenuPopover>
//                                                                 <MenuList>
//                                                                     {caseStatuses?.map((stat: CaseStatus) => (
//                                                                         <MenuItem
//                                                                             key={`${stat.systemStatus}-${stat.status}`}
//                                                                             onClick={() => completeAndChangeStatus(stat)}
//                                                                             data-testid='case-milestones-complete-change-status-menu-item'
//                                                                         >
//                                                                             {stat.status}
//                                                                         </MenuItem>
//                                                                     ))}
//                                                                 </MenuList>
//                                                             </MenuPopover>
//                                                         </Menu>
//                                                     )}
//                                                 </>
//                                             ) : (
//                                                 <>
//                                                     <MenuItem
//                                                         icon={<ArrowResetRegular />}
//                                                         onClick={onOpenReopenConfirmClick}
//                                                         data-testid='case-milestones-reopen-menu-item'
//                                                     >
//                                                         Reopen this Milestone
//                                                     </MenuItem>
//                                                 </>
//                                             )}
//                                         </MenuList>
//                                     </MenuPopover>
//                                 </Menu>
//                                 {readyToComplete() && (
//                                     <Badge
//                                         appearance='filled'
//                                         color='warning'
//                                         shape='circular'
//                                         size='small'
//                                         style={{
//                                             position: 'absolute',
//                                             top: '-10px',
//                                             left: '-10px',
//                                             transform: 'rotate(-20deg)',
//                                         }}
//                                     >
//                                         Ready
//                                     </Badge>
//                                 )}
//                             </Column>
//                             {milestoneObj.completed && (
//                                 <>
//                                     <Column gap='small' padding='pt-3' align='right'>
//                                         <Text size={200} className={completedTextClasses} italic>
//                                             Completed on: {utils.date.formatDate(milestoneObj.completedOn, {}, true)}
//                                         </Text>
//                                         <Text size={200} className={completedTextClasses} italic>
//                                             Completed by:{' '}
//                                             {userUtils.getUsersName(
//                                                 milestoneObj.completedBy!,
//                                                 primaryAccount?.accountUsers || [],
//                                             )}
//                                         </Text>
//                                     </Column>
//                                 </>
//                             )}
//                         </Column>
//                     </Row>
//                     <Column>
//                         <Row noGap>
//                             <Column flex='1' maxWidth='50%' padding='pr-2'>
//                                 <FormSection
//                                     title='Tasks'
//                                     action={
//                                         <Row>
//                                             <Column width='auto' vAlign='middle'>
//                                                 {milestoneObj?.completed &&
//                                                     !!taskService?.tasks
//                                                         ?.filter(filterTasksForMilestone)
//                                                         .some((x) => x.status == TaskStatus.open) && (
//                                                         <Badge appearance='filled' size='large' color='danger'>
//                                                             Open Tasks
//                                                         </Badge>
//                                                     )}
//                                             </Column>
//                                             <Column width='auto'>
//                                                 {access.cases.canCreateTasks() && (
//                                                     <AddEditTaskDialog
//                                                         onSubmit={taskService.upsertTaskAsync}
//                                                         initialCaseId={milestoneObj?.caseId}
//                                                         milestone={milestoneObj}
//                                                         canChangeCase={false}
//                                                     >
//                                                         <LinkButton
//                                                             icon={<AddCircle12Regular />}
//                                                             text='Add Task'
//                                                             data-testid='case-milestones-add-task-button'
//                                                         />
//                                                     </AddEditTaskDialog>
//                                                 )}
//                                             </Column>
//                                         </Row>
//                                     }
//                                 >
//                                     {!!taskService?.tasks?.filter(filterTasksForMilestone).length ? (
//                                         <>
//                                             {/* <CaseTaskTable
//                                                 tasks={
//                                                     taskService?.tasks
//                                                         ?.filter(filterTasksForMilestone)
//                                                         .sort(sortMilestoneTasks) || []
//                                                 }
//                                                 bodyHeight='12rem'
//                                                 showAssigned
//                                                 showBillable={false}
//                                                 showStatus
//                                                 showMilestone={false}
//                                                 timeTrackingService={timeTrackingService}
//                                                 select={false}
//                                             /> */}
//                                         </>
//                                     ) : (
//                                         <Column className={styles.emptyPanel} gap='medium' align='center'>
//                                             <Text size={500}>You are up to date!</Text>
//                                             <Text size={400}>There are no Tasks remaining.</Text>
//                                         </Column>
//                                     )}
//                                 </FormSection>
//                             </Column>
//                             <Column flex='1' maxWidth='50%' padding='pl-2'>
//                                 <FormSection
//                                     title='Events'
//                                     action={
//                                         <>
//                                             {access.cases.canAddEvents(milestoneObj.caseId) && (
//                                                 <AddEditCaseEventDialog
//                                                     onSubmit={eventService.upsertCaseEventAsync}
//                                                     caseId={milestoneObj?.caseId as string}
//                                                     milestone={milestoneObj}
//                                                 >
//                                                     <LinkButton
//                                                         icon={<AddCircle12Regular />}
//                                                         text='Add Event'
//                                                         data-testid='case-milestones-add-event-button'
//                                                     />
//                                                 </AddEditCaseEventDialog>
//                                             )}
//                                         </>
//                                     }
//                                 >
//                                     {!!eventService.events.filter(filterEventsForMilestone)?.length ? (
//                                         <>
//                                             <CaseEventsTable
//                                                 events={eventService.events.filter(filterEventsForMilestone)}
//                                                 bodyHeight='12rem'
//                                                 submitEvent={eventService.upsertCaseEventAsync}
//                                                 deleteEvent={onDeleteEvent}
//                                                 //onSelectionChanged={onSelectedEventsChanged}
//                                                 select={false}
//                                             />
//                                         </>
//                                     ) : (
//                                         <Column className={styles.emptyPanel} gap='medium' align='center'>
//                                             <Text size={500}>You are up to date!</Text>
//                                             <Text size={400}>There are no Events remaining.</Text>
//                                         </Column>
//                                     )}
//                                 </FormSection>
//                             </Column>
//                         </Row>
//                     </Column>
//                 </Column>
//                 <ConfirmationDialog
//                     icon={<ArrowResetRegular />}
//                     title={`Reopen Milestone #${(milestoneObj.order || 0) + 1} - ${milestoneObj.name}`}
//                     text={
//                         <Column gap='medium'>
//                             <Text size={400}>Are you sure you want to reopen this milestone?</Text>
//                             <Text size={300}>
//                                 Reopening this milestone will cause all subsequent milestones to be reopened as well.
//                             </Text>
//                         </Column>
//                     }
//                     open={reopenConfirmOpen}
//                     onOpenChange={onReopenConfirmOpen}
//                     onConfirm={clickReopen}
//                 />
//             </AccordionPanel>
//         </AccordionItem>
//     );
// };
